import { getDataWithDeduplication, getDataWithQueryString, postDataWithDeduplication } from '../../../api/componentAPIs';
import appConstants from '../../../appConstants';
import pickupOperationsLogger from './PickupEventHandler/PickupOperationsLogger';

const FETCH_ALL = '*';
const pickupOrProNumbersString = (array) => {
  const numbers = ` #${array.splice(0, 2).join(' #')}`;
  const moreNumbers = array.length ? `+${array.length} more ` : '';
  return `${numbers} ${moreNumbers}`;
};

const getDispatchRoutesQueryString = (driverIds) => {
  return driverIds?.length
    ? `&driverIds=${JSON.stringify(driverIds.map((driverId) => driverId.toUpperCase()))}`
    : '';
};

const getDriverRoutesData = (() => {
  let pendingDriverIds = [];
  let syncedDriverIds = []; //syncedDriverIds will help to restore pendingDrivers list in case of request rejection/failure
  let currentTrackingId = null; // Track the current pickup operation
  
  const updateDriverRoutesFailure = (driversToUpdate = []) => {
    // if data fetch failed , restore pending list to update in next call
    pendingDriverIds = [...new Set([...pendingDriverIds, ...driversToUpdate])];
  };

  const getLatestQuery = (terminalCode, originalDriverIdsToUpdate) => () => {
    // Record actual API call start
    if (currentTrackingId) {
      pickupOperationsLogger.startActualDispatchRoutesApiCall(currentTrackingId);
    }
    let driversToUpdate = [
      ...new Set([...pendingDriverIds, ...originalDriverIdsToUpdate, ...syncedDriverIds])
    ]; // Combine pending and original driver IDs and use it to generate latest query
    if (!originalDriverIdsToUpdate.length || pendingDriverIds.includes(FETCH_ALL)) {
      driversToUpdate = [];
      syncedDriverIds = [];
    } else {
      syncedDriverIds = [...pendingDriverIds]; // store pending drivers as syncedDrivers to restore in case of rejection of the latest call being made
    }

    pendingDriverIds = []; // reset pending list as its already added to the latest request's query.
    const driversQueryString = getDispatchRoutesQueryString(driversToUpdate);
    return `terminalCode=${terminalCode}${driversQueryString}`;
  };

  // Record API response
  const logCompleteDispatchRoutesRefreshTracking = (response) => {
    if (currentTrackingId) {
      pickupOperationsLogger.completeDispatchRoutesRefreshTracking(currentTrackingId, response?.config?.url);
      currentTrackingId = null; // Reset current tracking ID
    }
  };

  // Update tracking ID for pickup operations, or keep existing ID if request is cancelled due to deduplication
  const updateTrackingId = (pickupOperationTimeTrackingId) => { 
    currentTrackingId = pickupOperationTimeTrackingId || currentTrackingId;
    // Start tracking getAllDispatchRoutes deduplication
    if (currentTrackingId) {
      pickupOperationsLogger.startDispatchRoutesRefreshTracking(currentTrackingId);
    }
  };

  return (terminalCode, driverIds, pickupOperationTimeTrackingId) => {
    if (!driverIds.length) {
      pendingDriverIds = [FETCH_ALL];
    }
    updateTrackingId(pickupOperationTimeTrackingId);
    return getDataWithDeduplication(
      'DispatchedDrivers',
      null,
      getLatestQuery(terminalCode, driverIds) // call getLatestQuery function with original list of driverIds to allow inner function to access it when it is invoked later using closure.
    )
      .then((response) => {
        if (response.status !== 200) {
          // add rejected request's drivers to pending list.
          updateDriverRoutesFailure(driverIds);
        }
        else {
          syncedDriverIds = [];  //Clear syncedDriverIds only when previous request made with these ids gets resolved.
          logCompleteDispatchRoutesRefreshTracking(response);
        }
        return response;
      })
      .catch((err) => {
        // add rejected request's drivers to pending list.
        updateDriverRoutesFailure(driverIds);
        throw err;
      });
  };
})();

// TODO: Make it reusable as getAllDispatchRoutes and getAllPickupsData share the same logic.
const getAllPickupsData = (() => {
  let pendingPickupIds = [];
  let syncedPickupIds = []; //syncedPickupIds will help to restore pendingPickups list in case of request rejection/failure
  const updatePickupsFailure = (pickupsToUpdate = []) => {
    // if data fetch failed , restore pending list to update in next call
    pendingPickupIds = [...new Set([...pendingPickupIds, ...pickupsToUpdate])];
  };

  const getLatestQuery = (terminalCode, originalPickupIdsToUpdate) => () => {
    let pickupsToUpdate = [
      ...new Set([...pendingPickupIds, ...originalPickupIdsToUpdate, ...syncedPickupIds])
    ]; // Combine pending and original pickup IDs and use it to generate latest query
    if (!originalPickupIdsToUpdate.length || pendingPickupIds.includes(FETCH_ALL)) {
      pickupsToUpdate = [];
      syncedPickupIds = [];
    } else {
      syncedPickupIds = [...pendingPickupIds]; // store pending pickups as syncedPickups to restore in case of rejection of the latest call being made
    }

    pendingPickupIds = []; // reset pending list as its already added to the latest request's query.
    return {
      terminalCode,
      ...(pickupsToUpdate.length && { pickupIds: pickupsToUpdate }),
    };
  };

  return (terminalCode, pickupIds) => {
    if (!pickupIds.length) {
      pendingPickupIds = [FETCH_ALL];
    }
    return postDataWithDeduplication(
      'PickupsGrid',
      null,
      getLatestQuery(terminalCode, pickupIds) // call getLatestQuery function with original list of pickupIds to allow inner function to access it when it is invoked later using closure.
    )
      .then((response) => {
        if (response.status !== 200) {
          // add rejected request's pickups to pending list.
          updatePickupsFailure(pickupIds);
        }
        else {
          syncedPickupIds = [];  //Clear syncedPickupIds only when previous request made with these ids gets resolved.
        }
        return response;
      })
      .catch((err) => {
        // add rejected request's pickups to pending list.
        updatePickupsFailure(pickupIds);
        throw err;
      });
  };
})();

const getPickupDataFromEvent = (updatedPickups, eventName) => {
  return updatedPickups.map((pickup) => {
    return {
      ...pickup,
      driverId:
        eventName === appConstants.PICKUP_EVENTS.ASSIGN_PICKUP
          ? pickup.driverId.toUpperCase()
          : null,
      routeId: eventName === appConstants.PICKUP_EVENTS.ASSIGN_PICKUP ? pickup.routeId : null,
      driverNumber:
        eventName === appConstants.PICKUP_EVENTS.ASSIGN_PICKUP ? pickup.driverNumber : null,
      isAcknowledged: false,
      isToBeReviewed: pickup.isToBeReviewed, 
      action: undefined,
      showNotification: undefined,
      success: undefined
    };
  });
};

const getRouteMappingData = async (terminalId) => {
  try{
  const response = await getDataWithQueryString(
    "GetRouteMapping",
    `terminalId=${terminalId}`
  );
  if (response && response.status >= 200 && response.status < 400) {
      return response.data;
    }
    return [];
  } catch (error) {
    return [];
  }
};

const sanitizeQueryParams = (queryParams) => {
  const filteredParams = queryParams?.filter(Boolean);
  return filteredParams ? [...new Set(filteredParams)] : [];
}

export {
  pickupOrProNumbersString,
  getDriverRoutesData,
  getAllPickupsData,
  getPickupDataFromEvent,
  sanitizeQueryParams,
  getRouteMappingData
};
